import { Impersonation, UserSession } from '@b2b/common/services/auth/types';
import { assign, enqueueActions, fromCallback, fromEventObservable, setup } from 'xstate';

export type BufferablePendoEvents =
  | { type: 'USER_LOGGED_IN'; params: { session: UserSession; impersonation: Impersonation | null } }
  | { type: 'USER_LOGGED_OUT' }
  | { type: 'USER_CONTEXT_CHANGED'; params: { session: UserSession; impersonation: Impersonation | null } }
  | { type: 'TRACK_PAGE_LOAD'; params: Partial<{ routeName: string }> }
  | { type: 'TRACK_EVENT'; params: { eventName: string; metadata?: Record<string, unknown> } };

export type PendoReadinessEvents =
  | { type: 'PENDO_NOT_INSTALLED' }
  | { type: 'PENDO_STUB_DETECTED' }
  | { type: 'PENDO_AGENT_LOADED' }
  | { type: 'PENDO_READY' }
  | { type: 'PENDO_INITIALIZED'; params: { identity: PendoIdentity | null } }
  | { type: 'PENDO_INITIALIZATION_FAILED' }
  | { type: 'PENDO_IDENTIFY_FAILED' };

export type PendoEvents = PendoReadinessEvents | BufferablePendoEvents;

export interface PendoIdentity {
  session: UserSession;
  impersonation: Impersonation | null;
}

interface PendoContext {
  bufferedEvents: BufferablePendoEvents[];
  currentIdentity: PendoIdentity | null;
}

function isBufferedPendoEvent(event: PendoEvents): event is BufferablePendoEvents {
  return ['USER_LOGGED_IN', 'USER_LOGGED_OUT', 'USER_CONTEXT_CHANGED', 'TRACK_PAGE_LOAD', 'TRACK_EVENT'].includes(
    event.type,
  );
}

export const pendoMachine = setup({
  types: {
    events: {} as PendoEvents,
    context: {} as PendoContext,
  },

  actors: {
    authStateObservable: fromEventObservable<PendoEvents, void>(() => {
      throw new Error('authStateObservable must be provided');
    }),
    pendoReadinessMonitor: fromCallback(() => {
      throw new Error('pendoReadinessMonitor must be provided');
    }),
  },

  actions: {
    bufferEvent: assign(({ context, event }) => {
      return {
        bufferedEvents: isBufferedPendoEvent(event) ? [...context.bufferedEvents, event] : context.bufferedEvents,
      };
    }),
    clearBufferedEvents: assign({
      bufferedEvents: () => [],
    }),
    flushBufferedEvents: enqueueActions(({ enqueue, context }) => {
      const bufferedEvents = [...context.bufferedEvents];

      enqueue.assign({ bufferedEvents: () => [] });
      bufferedEvents.forEach((bufferedEvent) => enqueue.raise(bufferedEvent));
    }),
    assignCurrentIdentityFromInit: assign({
      currentIdentity: ({ event }) => (event.type === 'PENDO_INITIALIZED' ? event.params.identity : null),
    }),
    assignCurrentIdentityFromEvent: assign({
      currentIdentity: ({ event }) => {
        return event.type === 'USER_LOGGED_IN' || event.type === 'USER_CONTEXT_CHANGED' ? event.params : null;
      },
    }),
    clearCurrentIdentity: assign({ currentIdentity: () => null }),
    initializePendo: () => {
      // Implemented by service
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    identify: (_: unknown, params: { session: UserSession | null; impersonation: Impersonation | null }) => {
      // Implemented by service
    },
    clearSession: () => {
      // Implemented by service
    },
    trackPageLoad: () => {
      // Implemented by service
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    trackEvent: (_: unknown, params: { eventName: string; metadata?: Record<string, unknown> }) => {
      // Implemented by service
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAcwDsIHsB0BXNA1mpgO5oDEACgKIByAIgPID6tjAKswJK0DK7AQQAyQ6vQDaABgC6iFJlgBLAC6LMaOSAAeiAEwBOfdgAcARnMBWSQGYbkgCwB2YwBoQAT0QXTxkxYBs+hb2ko6Ouv66AL5RbqgYOPhEpBQ0DCz8AKoAQsz01OzUAMKFEjKayAoqahpI2nqGJuamVra2Tq4eiP7+jtj6tsbW-hbWtv72MXHoWHiExGRUdEzMAgDidJxCjAL5ZbJ1lUqq6po6CAZGZpZ27c5ungjWYdiSprqhxv7WugYW+lMQPFZkkFqlliwAErUXYATSkB3kxxqZwaV2arTsHQeiH0kl8xjxLQ6AXsZMBwMS8xS5EyvGokOY2zWG3o3FoCIqVROtVA50c-hMjn+Fl0xmFkn8b3sOIQjn0fVM1lMhl0ouGUuisSBMypyUWdIZTMYLLEzEYmXYnMO3JRdXO-l8osMQ2Cavso10sscb2wjpu9gMpkDAO1lLm+oohsZRUYtEKAA1OEUABICWis61I6qne2IQOyqWC3S2EumHrPYkU3URsHkdiQgRFADSzEo62oxt2WaBttzfO6Ric9n0ESC1nVFll3kk2DCA3ehOMYvl1YStZpDabreoADVNj2jjnefUEOL+hWQtZDCGlbL7C1sD8hvoH8+pRO1yC0Io0LBlAAhgANkBkBLOkzBZLk+SFCUYiHn2J4OoSc6EvoUo+j0Poyl0CDBH0HrNL0+IKpIWrTOu+C-v+wGgRA4ErB28ZdnsCHIv2p6OkYziGBhkrEThjwjgR+imMK8qiT4jjWJMYY1lRf6ASBYFpCs0Jwmxx6oggvTcWhfFYQ43rGL4D4OMKga9M6X44NRSl0QxLBsJwPD8MIoj7Fy7FId08qobxoT8dhspibofpjMqkiiYGaoWDZ2B2bRKkQpB7A5HkBTFKUmk8tp6FOgKD4+pIIo-MZpmmOZbpWQY8WJcp9GqSwTFbDsrHlDa3l5Y62AWIVYmSCV45erhoyCoR5jEYSPrkTq671Q5TXMOp9Dwh12a5XmCD5b1-XFaVI2PD4ZFPhMg2OOY5ljHVilJRA2DEMozAAE5gABEDuLS9KMsyrLsjldoDggLQqtgD7fAu7y6AWuGVT0YNSiVgSSjYQw3TRDUPZgT2ve9n3RsappshaVrrb2XVbSDpivNhFaOPYvT07KCrU4NonWGYIT+JVjjo-ZkBYzjb0fV9RqxvG1BJswqbppmZNHptQPBsY9i9SE9jKhdZijrK4oWL1FjLiqzzeEMs3hgtAuPS9wufVuLZth2LEAxx5wg2FbzBBOMkRGq-jGbOPO2CjCoWAEfN3YLNt4-WjYO3uB7y4h2ktLYvX8UEZK9JV+iFirfqkob3jqsuEeY9buMi0tLUscw7BcAAstQJMuz5wMc74iMDOKYo2M805jNgYmM6JAQyY6Zd0dglfuAlP6qMBigAF6-lAjnslw9fCFwABa8FJxTSuBr4Pgq9JJVqsMjghc4fTQ8Y+EPsYoRipPAsz3P1SLyvaBr0tPCby4NvHeAh65xmYAAMQEFwDyrcU7HyHiZJwthRRjWvrDdCfRRQPyDpKd4b97of1-F-ICy9V6ix+iaP6PA4GUw+PrS+E5fiZ3+DfQafpwg+iVAuX45sayW0IbbT+C9SE-zXgTX6ZoW4Hy0pTFUgo8TSSvLpV81gQpSgYboY2-xAjFwIdPIRxCRFkN-hQ6WcZEzJjTBmfeiJyayKVgNJ8YRrxRWlFFEKVhuIXQGsEf4+JrD6KIfPRQ39yH21bO2DYzsZGK1POYBUfhfgPh8CEXhIUHyqx+A4EqUNbCmCCYYkJYTTEROYAneMtClZaMFDJZ4ypg5DE6EdZUBJAj2C+BOR00k4pyXmrdTGRCICgTMZItkNDYmA3iRdfWzgnDhG5gKH4ph1H016uhd47xiKSl6RRWYAiDF4wSsMsAoyqFSMtFU6ZZJsARGcKMEyz8zCeNFLcic4lDYjDMIUo5igTlmPFpY6W1i5Z2IVlMt2MzULzIiMPZZGSDBCisLfGwoonA-I+sckZZSomdm2N2SZrtEBiQ9LcxcDSJQqhCmMVWgYNZKl0BJElGLZ5-OxXHHc+5KmErbnCucDhoYdI+KOJwIUSy+AVBOEk3xKp8P6RjKeQyRn-3yPGLgEDYSQOgbAnlKcFRYLIoysSPhbDoKOsufWg0xQtAFCRMSMRtTEAgHACouovIONPAAWn9rhb18VQQpHdXEt2lVwqoPed8Mi3xdaJMNmKcePgxICn9T+AZdEg0Qq8CEOchtQhPxwYJLN1MVYlUGoGEqDNjAEIzUS7aHxdoTAGkNT0spoYexVsw3SIxSz6IrrbGtvKZKzi7hzcIz8xhmsQD8IwwZs5YQ9CzXt2No6Yv-LgAARswdQQFHgbUze3AYfo2ajt7hO+8azAilgVB068eJ-BLqFkcgCMA0BPSApgd6kBmAkAAtUX+K7d32ODcS681gj1RRPeO-uuFwa3Kih8DUmdzAPoA9gZ96A30fogMwVQABbMAmBcDKAHSnR0YGGa9G7Z0lcA9yPBkNhzcwERyR9P2Wm9+-bOoepDb8Zx0k8REhCLnWGIN+jNBNj8RlEwWXCNCaI1eJG5GiT9K+Q29NFwdJCs-am7xx7+l6AYWSezbLscEb8k5imlbhGpsMIYyoRhKh9JO4G2n+gBGfp7Qa3wZNgGes9TAz1LOnl4Qw2wr4PgTGebDM2YNxSSX0E0rzDqohAA */
  id: 'pendo',

  context: () => {
    return {
      bufferedEvents: [],
      currentIdentity: null,
    };
  },

  initial: 'unknown',

  invoke: [
    { id: 'authStateWatcher', src: 'authStateObservable' },
    { id: 'pendoReadinessWatcher', src: 'pendoReadinessMonitor' },
  ],

  states: {
    unknown: {
      on: {
        PENDO_NOT_INSTALLED: {
          target: 'uninstalled',
          actions: ['clearBufferedEvents'],
        },
        PENDO_STUB_DETECTED: { target: 'installed.not_ready.stub_only' },
        PENDO_AGENT_LOADED: { target: 'installed.not_ready.agent_loaded_waiting_ready' },
        PENDO_READY: { target: 'installed.ready' },

        USER_LOGGED_IN: { actions: ['bufferEvent'] },
        USER_LOGGED_OUT: { actions: ['bufferEvent'] },
        USER_CONTEXT_CHANGED: { actions: ['bufferEvent'] },
        TRACK_PAGE_LOAD: { actions: ['bufferEvent'] },
        TRACK_EVENT: { actions: ['bufferEvent'] },
      },
    },

    uninstalled: {
      on: {
        PENDO_STUB_DETECTED: { target: 'installed.not_ready.stub_only' },
        PENDO_AGENT_LOADED: { target: 'installed.not_ready.agent_loaded_waiting_ready' },
        PENDO_READY: { target: 'installed.ready' },
      },
    },

    installed: {
      on: {
        PENDO_NOT_INSTALLED: {
          target: 'uninstalled',
          actions: ['clearBufferedEvents'],
        },
        PENDO_STUB_DETECTED: { target: '.not_ready.stub_only' },
        PENDO_AGENT_LOADED: { target: '.not_ready.agent_loaded_waiting_ready' },
        PENDO_READY: { target: '.ready' },
      },

      initial: 'not_ready',

      states: {
        not_ready: {
          initial: 'stub_only',

          on: {
            USER_LOGGED_IN: { actions: ['bufferEvent'] },
            USER_LOGGED_OUT: { actions: ['bufferEvent'] },
            USER_CONTEXT_CHANGED: { actions: ['bufferEvent'] },
            TRACK_PAGE_LOAD: { actions: ['bufferEvent'] },
            TRACK_EVENT: { actions: ['bufferEvent'] },
          },

          states: {
            stub_only: {
              after: {
                30_000: { target: 'agent_load_timeout' },
              },
            },
            agent_loaded_waiting_ready: {
              // Call initialize() here so the SDK can complete its startup and fire events.ready.
              // PENDO_READY will arrive once the SDK is fully initialized.
              entry: ['initializePendo'],

              on: {
                PENDO_INITIALIZED: {
                  // Stay in this state — just record who was initialized while waiting for PENDO_READY
                  actions: ['assignCurrentIdentityFromInit'],
                },
                PENDO_INITIALIZATION_FAILED: { target: 'agent_load_timeout' },
              },
            },
            agent_load_timeout: {},
          },
        },

        ready: {
          initial: 'idle',

          states: {
            idle: {
              entry: ['flushBufferedEvents'],

              on: {
                USER_LOGGED_IN: {
                  actions: [
                    {
                      type: 'identify',
                      params: ({ event }: { event: Extract<PendoEvents, { type: 'USER_LOGGED_IN' }> }) => event.params,
                    },
                    'assignCurrentIdentityFromEvent',
                  ],
                },
                USER_LOGGED_OUT: {
                  actions: ['clearSession', 'clearCurrentIdentity'],
                },
                USER_CONTEXT_CHANGED: {
                  actions: [
                    {
                      type: 'identify',
                      params: ({ event }: { event: Extract<PendoEvents, { type: 'USER_CONTEXT_CHANGED' }> }) => {
                        return event.params;
                      },
                    },
                    'assignCurrentIdentityFromEvent',
                  ],
                },
                TRACK_PAGE_LOAD: { actions: 'trackPageLoad' },
                TRACK_EVENT: {
                  actions: {
                    type: 'trackEvent',
                    params: ({ event }: { event: Extract<PendoEvents, { type: 'TRACK_EVENT' }> }) => event.params,
                  },
                },
                PENDO_IDENTIFY_FAILED: { target: 'error' },
              },
            },

            error: {},
          },
        },
      },
    },
  },
});
